import React from "react";
import PropTypes from "prop-types";
import { Chip, Tooltip } from "@material-ui/core";

/**
 * @param {Object} props
 * @returns {React.Component}
 */
const CustomChip = ({
  label,
  icon,
  color,
  size,
  variant,
  withTooltip,
  tooltipTitle,
  tooltipPlacement,
  backgroundColor,
  textColor,
  borderColor,
  customStyles,
  active,
  clickable,
  onDelete,
  deleteIcon,
  avatar,
  disabled,
  onClick,
  ...otherProps
}) => {
  const chipStyles = {
    backgroundColor:
      backgroundColor || (color !== "default" ? undefined : "#e0e0e0"),
    color: textColor || "#000",
    boxShadow: "none",
    border: 0,
    height: size === "small" ? "12px" : "18px",
    fontSize: size === "small" ? "0.55rem" : "0.65rem",
    fontWeight: "bold",
    display: "inline-flex",
    alignItems: "center",
    paddingLeft: "4px",
    paddingRight: "4px",
    maxWidth: "100%",
    ...(active !== undefined && {
      opacity: active ? 1 : 0.6,
    }),
    ...(borderColor && {
      border: `0px solid ${borderColor}`,
    }),
    ...customStyles,
  };

  const iconStyles = {
    fontSize: size === "small" ? "13px" : "16px",
    color: textColor || "#000",
    marginRight: "2px",
  };

  const labelStyles = {
    alignItems: "center",
    justifyContent: "center",
    color: textColor,
    lineHeight: "1",
    padding: "0 1px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    maxWidth: "100%",
  };

  // ✅ MUI v4: Chip só aceita "default" ou "outlined", mapear "filled" para "default"
  const muiVariant = variant === "filled" ? "default" : variant;

  const chip = (
    <Chip
      label={
        typeof label === "string" ? (
          <span style={labelStyles}>{label.toUpperCase()}</span>
        ) : (
          label
        )
      }
      icon={icon && React.cloneElement(icon, { style: iconStyles })}
      size={size}
      variant={muiVariant}
      color={color}
      clickable={clickable}
      onDelete={onDelete}
      deleteIcon={deleteIcon}
      avatar={avatar}
      disabled={disabled}
      onClick={onClick}
      style={chipStyles}
      {...otherProps}
    />
  );

  return withTooltip ? (
    <Tooltip
      title={tooltipTitle || ""}
      placement={tooltipPlacement || "top"}
    >
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

CustomChip.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  icon: PropTypes.element,
  color: PropTypes.oneOf([
    "default",
    "primary",
    "secondary",
    "error",
    "info",
    "success",
    "warning",
  ]),
  size: PropTypes.oneOf(["small", "medium"]),
  variant: PropTypes.oneOf(["default", "outlined", "filled"]), // ✅ MUI v4: "default" ou "outlined" (filled é mapeado para default)
  withTooltip: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  tooltipPlacement: PropTypes.oneOf([
    "bottom-end",
    "bottom-start",
    "bottom",
    "left-end",
    "left-start",
    "left",
    "right-end",
    "right-start",
    "right",
    "top-end",
    "top-start",
    "top",
  ]),
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  borderColor: PropTypes.string,
  customStyles: PropTypes.object,
  active: PropTypes.bool,
  clickable: PropTypes.bool,
  onDelete: PropTypes.func,
  deleteIcon: PropTypes.element,
  avatar: PropTypes.element,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
};

CustomChip.defaultProps = {
  color: "default",
  size: "medium",
  variant: "filled",
  withTooltip: false,
  active: true,
  clickable: false,
  disabled: false,
};

export default CustomChip;
